import { ArrowLeftIcon, ArrowLineDownIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'; // Added useLocation
import TorchGuide from '../components/generic/TorchGuide';
import GlobalSidebarItem from '../components/sidebar/GlobalSidebar/GlobalSidebarItem';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
import { useMediaQuery } from '../utils/screenWidth';
import AttemptAnalysis from './components/AttemptAnalysis';
import CombinedOverview from './components/combinedAnalysis/CombinedOverview';
import DifficultyAnalysis from './components/DifficultyAnalysis';
import PerformanceAnalysis from './components/PerformanceAnalysis';
import QuestionByQuestionAnalysis from './components/QuestionByQuestionAnalysis';
import QuestionJourney from './components/QuestionJourney';
import ScorePotential from './components/ScorePotential';
import SubjectMovement from './components/SubjectMovement';
import downloadAnalysis from './services/downloadAnalysis';
import { toast } from 'react-hot-toast';
import { ThemeContext } from '../../../../apps/frontend/src/context/ThemeStore';

const steps = [
  {
    target: '.comparative-target',
    content: 'Click here to view comparative analysis and compare your performance with other students.',
    title: 'View Comparative Analysis',
    disableBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '.detailed-analysis-target',
    content: 'Click here to view different types of analysis to evaluate your performance better.',
    title: 'Detailed Analysis',
    placement: 'right',
  },
  {
    target: '.download-analysis-target',
    content: 'Click here to download the "Detailed Analysis" or "Quizrr AI Analysis".',
    title: 'Download Analysis',
    spotlightPadding: 5,
  },
];

// Download Analysis Modal Component
function DownloadAnalysisOptionsModal({ testsData, open, onOpenChange, onSelectPaper }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const modalRef = useRef(null);

  const clickHandler = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onOpenChange(false);
    }
  };

  if (!open) return null;

  if (isDesktop) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/20"
        onClick={clickHandler}
      >
        <div ref={modalRef} className="relative bg-[var(--q3-surface-default)] rounded-2xl p-6 max-w-md w-full mx-4">
          <button
            className="absolute z-[1000] -top-12 left-1/2 -translate-x-1/2 p-2 rounded-full bg-[#161616] flex items-center justify-center cursor-pointer hover:scale-[1.01] shadow-md border "
            onClick={() => onOpenChange(false)}
          >
            <XIcon size={20} color="white" />
          </button>

          <div className="flex flex-col items-center text-center gap-4 pt-4">
            <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]">Download Analysis</h3>
            <p className="text-sm text-[var(--q3-neutral-light)]">Choose the paper you want to download</p>

            <div className="w-full flex flex-col gap-3 mt-2">
              {testsData?.map((test) => (
                <Button
                  key={test.testId}
                  variant="outline"
                  className="w-full rounded-lg h-12 flex items-center justify-between px-4 border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                  onClick={() => {
                    onSelectPaper(test.testId);
                    onOpenChange(false);
                  }}
                >
                  <span className="text-[var(--q3-neutral-default)] font-medium">{test.testName} Analysis</span>
                  <ArrowLineDownIcon size={18} color="var(--q3-accent-normal)" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile: Bottom drawer
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm bg-black/20"
      onClick={clickHandler}
    >
      <div
        ref={modalRef}
        className="relative z-[1000] bg-[var(--q3-surface-default)] rounded-t-2xl p-6 w-full max-h-[80vh]"
      >
        <button
          className="absolute z-[1000] -top-12 left-1/2 -translate-x-1/2 p-2 rounded-full bg-[#161616] flex items-center justify-center cursor-pointer hover:scale-[1.01] hover:bg-[#2a2a2a] shadow-md border"
          onClick={() => onOpenChange(false)}
        >
          <XIcon size={20} color="white" />
        </button>

        <div className="flex flex-col items-center text-center gap-4 pt-4">
          <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]">Download Analysis</h3>
          <p className="text-sm text-[var(--q3-neutral-light)]">Choose the paper you want to download</p>

          <div className="w-full flex flex-col gap-3 mt-2">
            {testsData?.map((test) => (
              <Button
                key={test.testId}
                variant="outline"
                className="w-full rounded-lg h-12 flex items-center justify-between px-4 border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                onClick={() => {
                  onSelectPaper(test.testId);
                  onOpenChange(false);
                }}
              >
                <span className="text-[var(--q3-neutral-default)] font-medium">{test.testName} Analysis</span>
                <ArrowLineDownIcon size={18} color="var(--q3-accent-normal)" />
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TestAnalysisCombined() {
  const { packId, testIdOne, testIdTwo } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Added useLocation hook
  const [cookies] = useCookies();
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDownloadOptionsModal, setShowDownloadOptionsModal] = useState(false);
  const { isDarkTheme } = useContext(ThemeContext);

  const user = JSON.parse(localStorage.getItem('userProfile'));
  const headers = sessionStorage.getItem('token') ? { Authorization: `Bearer ${sessionStorage.getItem('token')}` } : {};

  // tour guide states
  const [stepIndex, setStepIndex] = useState(0);
  const [showTorchGuide, setShowTorchGuide] = useState(true);

  // loading state
  const [isLoading, setIsLoading] = useState(true);

  // test states
  const [test, setTest] = useState(null);
  const [totalMarks, setTotalMarks] = useState(null);

  // download analysis state
  const [downloadingAnalysisDisable, setdownloadingAnalysisDisable] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // state for comparative and personal analysis
  const [hideComparativeAnalysis, setHideComparativeAnalysis] = useState(true);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('personal');

  // state for selected tab
  const [tabsToShow, setTabsToShow] = useState([]);
  const [tabIconsMapping, setTabIconsMapping] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);

  // check if analysis is avaiable
  const [detailedAnalysisAvailable, setDetailedAnalysisAvailable] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // analysis states
  const [percentile, setPercentile] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [comparativeData, setComparativeData] = useState(null);
  const [paidFeatures, setPaidFeatures] = useState(null);

  // Get category ID from URL params or location state
  const categoryId = searchParams.get('categoryId') || location.state?.selectedCategoryId;
  const source = searchParams.get('source') || location.state?.source;

  // Scroll states for hide-on-scroll behavior
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollContainerRef = useRef(null);
  const [testsData, setTestsData] = useState([]);

  const [combinedtestTitle, setcombinedTestTitle] = useState('');

  // Function to handle back navigation with category ID
  const handleBackNavigation = () => {
    if (categoryId) {
      navigate(`/v3/${packId}/tests?categoryId=${categoryId}`, {
        state: { selectedCategoryId: categoryId },
      });
    } else {
      if (source === 'timeline_view') {
        navigate(`/v3/${packId}/timelineview`);
        return;
      }
      navigate(`/v3/${packId}/tests`);
    }
  };

  function calculatePaperTotalScore(paperId, subjectWiseData) {
    let totalScore = 0;

    for (const subject of subjectWiseData) {
      if (!subject.tests) continue;

      for (const test of subject.tests) {
        if (test.testId === paperId) {
          totalScore += test.totalScore || 0;
        }
      }
    }

    return totalScore;
  }

  useEffect(() => {
    const checkIfTablet = () => {
      setShowTorchGuide(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  // Scroll handler for hide-on-scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024; // lg breakpoint

      if (!isTabletOrBelow) {
        // Always show header on desktop
        setIsHeaderVisible(true);
        return;
      }

      // Hide/show logic for tablet and mobile
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const testIds = `${testIdOne},${testIdTwo}`;

        const [metaRes, overviewRes] = await Promise.all([
          axios.get(`/api/test/combined/${packId}/tests-meta`, {
            params: { testIds: testIds },
          }),
          axios.get(`/api/test/combined/${packId}/overview`, {
            params: { testIds: testIds },
          }),
        ]);

        const metaList = metaRes?.data?.linkedTestsMeta || [];
        setcombinedTestTitle(metaRes?.data?.title || '');

        const getPaperName = (title = '') => {
          const match = title.match(/Paper\s*\d+/i);
          if (match) return match[0];
          return title.split(' ').slice(-2).join(' ');
        };

        const builtTestsData = metaList.map((meta) => ({
          testId: meta._id,
          testName: getPaperName(meta.shortTitle || meta.title),
          totalTime: meta.totalTime,
        }));

        setTestsData(builtTestsData);

        const raw = overviewRes?.data?.linkedTestsOverview;

        setTabsToShow(raw?.tabsToShow);
        setTabIconsMapping(raw?.tabIconsMapping);
        setSelectedTab(raw?.tabsToShow?.[0]);

        setHideComparativeAnalysis(!raw?.showComparativeAnalysis);

        const marks = {
          overall: raw?.overviewData?.totalScore,
          ...(raw?.subjectWiseData?.reduce((acc, subject) => {
            acc[subject.subjectId] = subject.totalScore;
            return acc;
          }, {}) || {}),
        };
        setTotalMarks(marks);

        const normalizedOverview = {
          overallData: {
            userRank: raw?.leaderboardData?.userRank,
            totalRanks: raw?.leaderboardData?.totalRanks,
            markScored: raw?.overviewData?.totalMarkScored,
            totalScore: raw?.overviewData?.totalScore,
          },
          overview: {
            questionAttempted: raw?.overviewData?.questionsAttempted,
            totalQuestions: raw?.overviewData?.totalQuestions,
            accuracy: raw?.overviewData?.accuracy,
            positiveScore: raw?.overviewData?.positiveScore,
            marksLost: raw?.overviewData?.negativeScore,
            totalScore: raw?.overviewData?.totalScore,
          },
          subjectWiseData: raw?.subjectWiseData?.map((subject) => ({
            ...subject,
            papers: subject.tests?.map((t) => {
              const testMeta = metaList.find((m) => m._id === t.testId);
              return {
                label: testMeta?.shortTitle || testMeta?.title || t.testId,
                markScored: t.markScored,
                totalScore: t.totalScore,
              };
            }),
          })),
          percentileObject: {
            ...raw?.percentileObject,
          },
          marksBreakdown: raw?.overviewData?.marksBreakdown?.map((t) => {
            const testMeta = metaList.find((m) => m._id === t.testId);
            return {
              title: testMeta?.shortTitle || testMeta?.title,
              markScored: t.score,
              totalScore: calculatePaperTotalScore(t.testId, raw?.subjectWiseData),
            };
          }),
          showAfterAllAttemptsMsg: false,
          afterAllAttemptsMsg: '',
        };
        setOverviewData(normalizedOverview);

        setTest({
          title: metaList[0]?.title || '',
          analysisVideo: raw?.analysisVideo?.video || null,
          analysisVideoCdn: raw?.analysisVideo?.cdn || null,
          analysisVideoThumbnail: isDarkTheme
            ? raw?.analysisVideo?.thumbnailDark || raw?.analysisVideo?.thumbnail
            : raw?.analysisVideo?.thumbnail,
          totalTime: metaList[0]?.totalTime || null,
          basicAnalysis: { notes: null },
        });

        setShowAnalysis(true);

        window.webengage.track('Viewed Detailed Analysis', {
          'User Id': user?.email,
          'Pack Id': packId,
          'Test Id': testIdOne,
          'Test Name': metaList[0]?.title,
          Source: searchParams.get('source'),
        });
      } catch (err) {
        navigate('/v2/home');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [packId, testIdOne, testIdTwo]);

  useEffect(() => {
    if (activeAnalysisTab === 'comparative') {
      setSelectedTab('Performance Analysis');
      if (!comparativeData) {
        setIsLoading(true);
        const testIds = `${testIdOne},${testIdTwo}`;
        axios
          .get(`/api/test/combined/${packId}/comparative-analysis`, {
            params: { testIds: testIds },
          })
          .then((res) => {
            setComparativeData(res?.data);
            setIsLoading(false);
          })
          .catch((err) => {
            setIsLoading(false);
            setActiveAnalysisTab('personal');
            setSelectedTab(tabsToShow?.[0]);
            toast('Comparative analysis will be available soon!');
          });
      }
    } else {
      setSelectedTab(tabsToShow?.[0]);
    }
  }, [activeAnalysisTab]);

  const handleDownloadAnalysis = async () => {
    // For combined analysis, show modal to select paper
    setShowDownloadOptionsModal(true);
  };

  const handleSelectPaper = async (testId) => {
    await downloadAnalysis(testId, packId, user._id, cookies.token, setdownloadingAnalysisDisable);

    window.webengage.track('Downloaded Resource', {
      'User Id': user?.email,
      'Pack Id': packId,
      'Test Id': testId,
      'Test Name': test?.title,
      'Resource Name': 'Analysis PDF',
    });
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  const handleJoyrideCallback = async (data) => {
    const { status, index, type, action } = data;

    if (status === 'finished') {
      setStepIndex(0);
      await axios.patch('/api/user/profile', {
        analysisV3TourDone: true,
      });
      localStorage.setItem('userProfile', JSON.stringify({ ...user, analysisV3TourDone: true }));
    } else if (type === 'step:after') {
      if (action === 'next') {
        setStepIndex(index + 1);
      } else if (action === 'prev') {
        setStepIndex(Math.max(0, index - 1));
      }
    }
  };

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="min-[1250px]:hidden q3-glass fixed border-t left-0 right-0 lg:left-[102px] bottom-0 z-20 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] p-4">
        <div className="flex flex-row">
          {!hideComparativeAnalysis && (
            <div className="flex flex-row">
              <Select
                value={activeAnalysisTab === 'comparative' ? 'Comparative' : 'Personal'}
                onValueChange={(val) => {
                  setActiveAnalysisTab(val.toLowerCase());
                }}
              >
                <SelectTrigger
                  className={`rounded-full flex items-center gap-2 px-4 py-2 bg-[var(--q3-surface-default)] text-[var(--q3-accent-normal)] border border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dimmest)] transition-colors w-auto cursor-pointer [&>svg]:text-[var(--q3-accent-normal)]`}
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent className="rounded-md border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] shadow-md max-h-64 overflow-y-auto cursor-pointer">
                  <SelectGroup>
                    {[
                      {
                        key: 'personal',
                        value: 'Personal',
                      },
                      {
                        key: 'comparative',
                        value: 'Comparative',
                      },
                    ].map((item) => (
                      <SelectItem key={item.key} value={item.value}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <div className="border-r ml-[11px]" />
            </div>
          )}
          <div className="flex flex-row gap-[11px] ml-[11px] overflow-x-auto">
            {activeAnalysisTab === 'personal'
              ? tabsToShow?.map((tab) => {
                  return (
                    <Button
                      variant={'outline'}
                      className={`rounded-full ${selectedTab === tab ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                      onClick={() => handleTabChange(tab)}
                      key={`tab-${tab}`}
                    >
                      {tab}
                      {selectedTab === tab && <CheckIcon />}
                    </Button>
                  );
                })
              : ['Performance Analysis', 'Attempt Analysis', 'Difficulty Analysis'].map((tab) => {
                  return (
                    <Button
                      variant={'outline'}
                      className={`rounded-full ${selectedTab === tab ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                      onClick={() => handleTabChange(tab)}
                      key={`tab-${tab}`}
                    >
                      {tab}
                      {selectedTab === tab && <CheckIcon />}
                    </Button>
                  );
                })}
          </div>
        </div>
      </div>

      {/* Torch Guide */}
      {!user?.analysisV3TourDone && showTorchGuide && (
        <TorchGuide steps={steps} handleTorchGuideCallback={handleJoyrideCallback} stepIndex={stepIndex} />
      )}

      <div className="flex flex-row max-w-[1300px] mx-auto flex-1 m-0 p-0 overflow-hidden">
        {/* Side Section */}
        <div className="hidden min-[1250px]:flex flex-col w-[264px] items-start pl-6">
          <div className="py-6 px-4 flex flex-col items-start">
            <div
              className="text-[var(--q3-neutral-default)] cursor-pointer bg-[var(--q3-surface-dimmer)] rounded-full p-2"
              onClick={handleBackNavigation} // Updated to use the new function
            >
              <ArrowLeftIcon size={24} />
            </div>
            <div className="text-[var(--q3-neutral-default)] text-2xl font-bold my-2">Test Analysis</div>
            <div className="text-sm text-[var(--q3-neutral-light)]">{combinedtestTitle}</div>
          </div>
          <div className="w-full">
            {!hideComparativeAnalysis ? (
              <Tabs value={activeAnalysisTab} onValueChange={(value) => setActiveAnalysisTab(value)}>
                <TabsList className="w-full mb-2">
                  <TabsTrigger value="personal">
                    Personal {activeAnalysisTab === 'personal' && <CheckIcon size={20} weight="bold" />}
                  </TabsTrigger>
                  <TabsTrigger value="comparative" className="pl-3 pr-2 py-2 comparative-target">
                    Comparative {activeAnalysisTab === 'comparative' && <CheckIcon size={20} weight="bold" />}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="personal" className={'detailed-analysis-target'}>
                  {tabsToShow?.map((tab, index) => (
                    <GlobalSidebarItem
                      key={index}
                      icon={tabIconsMapping?.[tab]}
                      title={tab}
                      isSelected={tab === selectedTab}
                      onClick={() => handleTabChange(tab)}
                      className={`w-full`}
                    />
                  ))}
                </TabsContent>
                <TabsContent value="comparative">
                  {['Performance Analysis', 'Attempt Analysis', 'Difficulty Analysis'].map((tab, index) => (
                    <GlobalSidebarItem
                      key={index}
                      icon={tabIconsMapping?.[tab]}
                      title={tab}
                      isSelected={tab === selectedTab}
                      onClick={() => handleTabChange(tab)}
                      className="w-full"
                    />
                  ))}
                </TabsContent>
              </Tabs>
            ) : (
              <div>
                {tabsToShow?.map((tab, index) => (
                  <GlobalSidebarItem
                    key={index}
                    icon={tabIconsMapping?.[tab]}
                    title={tab}
                    isSelected={tab === selectedTab}
                    onClick={() => handleTabChange(tab)}
                    className="w-full"
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col w-full h-screen overflow-y-auto" ref={scrollContainerRef}>
          <div
            className={`z-10 flex flex-col lg:flex-row justify-between p-6 pb-4 q3-glass rounded transition-transform duration-300 ease-in-out lg:translate-y-0 ${
              isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
            }`}
          >
            <div className="hidden lg:flex gap-2 flex-col items-start">
              <p className="text-2xl text-[var(--q3-neutral-default)] font-bold whitespace-nowrap">{selectedTab}</p>
            </div>
            <div className="flex w-full items-start justify-between">
              <div
                className="block lg:hidden cursor-pointer text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dimmer)] rounded-full p-2"
                onClick={handleBackNavigation} // Updated to use the new function
              >
                <ArrowLeftIcon size={24} />
              </div>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  className="group relative inline-flex h-10 overflow-hidden rounded-full p-[1px] focus:outline-none download-analysis-target"
                  onClick={handleDownloadAnalysis}
                >
                  <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
                  <span className="relative inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-[var(--q3-surface-dim)] px-3 py-1 text-sm font-medium text-[var(--q3-neutral-default)] backdrop-blur-3xl overflow-hidden">
                    <ArrowLineDownIcon
                      size={22}
                      className="transition-transform duration-300 group-hover:translate-y-0.5"
                    />
                    <div className="hidden lg:block relative ml-2 h-[1.25rem] overflow-hidden">
                      <div className={`transition-transform duration-500 ease-in-out translate-y-0`}>
                        <p className="h-[1.25rem] flex items-center">Download Analysis</p>
                      </div>
                    </div>
                  </span>
                </Button>
              </div>
            </div>

            <div className="lg:hidden mt-2 flex flex-col gap-2 items-start">
              <div>
                <p className="text-2xl text-[var(--q3-neutral-default)] font-bold">{selectedTab}</p>
              </div>
            </div>
          </div>
          <div className="p-6 pt-4 pb-28">
            {selectedTab === 'Overview' ? (
              <CombinedOverview
                overviewData={overviewData}
                percentile={percentile}
                analysisVideo={test?.analysisVideo}
                analysisVideoCdn={test?.analysisVideoCdn}
                analysisVideoThumbnail={test?.analysisVideoThumbnail}
                notes={test?.basicAnalysis?.notes}
                testTitle={test?.title}
                packId={packId}
                testId={testIdOne}
              />
            ) : selectedTab === 'Performance Analysis' ? (
              <PerformanceAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                totalMarks={totalMarks}
                packId={packId}
                testId={testIdOne}
                testIdTwo={testIdTwo}
                isCombined={true}
              />
            ) : selectedTab === 'Score Potential' ? (
              <ScorePotential packId={packId} testId={testIdOne} testIdTwo={testIdTwo} isCombined={true} />
            ) : selectedTab === 'Attempt Analysis' ? (
              <AttemptAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                packId={packId}
                testId={testIdOne}
                testIdTwo={testIdTwo}
                isCombined={true}
              />
            ) : selectedTab === 'Difficulty Analysis' ? (
              <DifficultyAnalysis
                packId={packId}
                testId={testIdOne}
                testIdTwo={testIdTwo}
                isCombined={true}
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
              />
            ) : selectedTab === 'Subject Movement' ? (
              <div className="overflow-x-auto">
                <SubjectMovement
                  packId={packId}
                  testId={testIdOne}
                  testIdTwo={testIdTwo}
                  isCombined={true}
                  testsData={testsData}
                />
              </div>
            ) : selectedTab === 'Question Journey' ? (
              <QuestionJourney
                packId={packId}
                testId={testIdOne}
                testIdTwo={testIdTwo}
                isCombined={true}
                totalTime={test?.totalTime}
                testsData={testsData}
              />
            ) : selectedTab === 'Qs by Qs Analysis' ? (
              <QuestionByQuestionAnalysis
                packId={packId}
                testId={testIdOne}
                testIdTwo={testIdTwo}
                isCombined={true}
                testsData={testsData}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
        <DownloadAnalysisOptionsModal
          testsData={testsData}
          open={showDownloadOptionsModal}
          onOpenChange={setShowDownloadOptionsModal}
          onSelectPaper={handleSelectPaper}
        />
      </div>
    </AppLayout>
  );
}
